/** @file node.remove: removes a node (children reparented or removed), its drawables, sockets, twins. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { unknownNodeWarnings } from '../clip/unknownNodeWarnings';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { orderNodes } from './orderNodes';
import { requireNode } from './requireNode';
import { subtreeOf } from './subtreeOf';

function checkAttachments(p: Puppet, removed: Set<string>): void {
  (p.attachments ?? []).forEach((a, i) => {
    const refs = [...Object.values(a.nodeMap), a.when?.hostNode, a.when?.parent, a.driver];
    const hit = refs.find((n) => n !== undefined && removed.has(n));
    if (hit !== undefined) {
      throwError(ERROR_CODES.VALIDATION, { node: hit, attachment: a.id }, { path: `/puppet/attachments/${i}`, hint: `attachment.detach ${a.id} first` });
    }
  });
}

/** Drops every reference to a removed node: twin links, drawables, anchor nodes and sockets. */
function dropReferences(p: Puppet, removed: Set<string>): void {
  for (const n of Object.values(p.nodes)) if (n.mirror !== undefined && removed.has(n.mirror)) delete n.mirror;
  p.drawables = p.drawables.filter((d) => !removed.has(d.node));
  for (const d of p.drawables) if (d.kind === 'impostor' && d.anchor?.node !== undefined && removed.has(d.anchor.node)) delete d.anchor.node;
  p.sockets = Object.fromEntries(Object.entries(p.sockets).filter(([, n]) => !removed.has(n)));
}

function clipWarnings(draft: PuppetDoc, removed: Set<string>): Warning[] {
  return Object.entries(draft.clips).flatMap(([name, clip]) =>
    unknownNodeWarnings(draft.puppet, name, clip).filter((w) => removed.has(String(w.params?.node))));
}

/**
 * `node.remove { name, children? }` → `{ removed }`: `reparent` (default) moves the children to the removed node's
 * parent with `pos += removed.pos`; `remove` drops the subtree. A node an attachment names cannot go. Inverse P.
 */
export const nodeRemoveCommand: CommandDef<'node.remove'> = {
  name: 'node.remove',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['name', 'children']);
    return compact({ name: r.str('name'), children: r.opt('children', (k) => r.oneOf(k, ['reparent', 'remove'] as const)) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const node = requireNode(p, input.name, '/input/name');
    if (node.parent === undefined) throwError(ERROR_CODES.VALIDATION, { node: input.name }, { path: '/input/name', hint: 'the root cannot be removed' });
    const removedList = input.children === 'remove' ? subtreeOf(p, input.name) : [input.name];
    const removed = new Set(removedList);
    checkAttachments(p, removed);
    const inverse = puppetInverse(draft);
    for (const child of Object.values(p.nodes)) {
      if (child.parent !== input.name || input.children === 'remove') continue;
      child.parent = node.parent;
      child.pos = [child.pos[0] + node.pos[0], child.pos[1] + node.pos[1], child.pos[2] + node.pos[2]];
    }
    p.nodes = orderNodes(Object.fromEntries(Object.entries(p.nodes).filter(([n]) => !removed.has(n))));
    dropReferences(p, removed);
    checkPuppet(draft, ctx);
    return { result: { removed: removedList }, warnings: clipWarnings(draft, removed), inverse };
  },
};
